/**
 * Smart defaults for the "New Game" form.
 *
 * Most league schedules are repetitive: games run on the same weekday(s), in
 * back-to-back slots of a fixed length, and the matchups work through a round
 * robin. This module reads the games already on a season's schedule, infers
 * that pattern, and proposes the next game so the admin usually only has to
 * confirm the form rather than fill it in.
 *
 * Everything here is a suggestion — the form stays fully editable, and every
 * suggestion carries a short human-readable reason so the admin can see why a
 * value was chosen.
 *
 * IMPORTANT: Game dates are "wall clock" times without timezone info (see
 * dateUtils). All arithmetic below is calendar-based (via the Date constructor,
 * which normalises overflow) rather than millisecond-based, so adding a week or
 * an hour never drifts across a daylight saving boundary.
 */

import { Game, Team } from "../types/models";
import {
  formatDateForInput,
  formatGameDateWithWeekday,
  formatGameTime,
  formatWeekdayLong,
} from "./dateUtils";

/** Slot length assumed when the schedule has no back-to-back games to learn from. */
const DEFAULT_SLOT_MINUTES = 60;

/** Gaps longer than this are treated as unrelated blocks of ice, not the next slot. */
const MAX_SLOT_MINUTES = 6 * 60;

/** How far ahead to look for the next scheduled weekday before falling back to a week. */
const MAX_LOOKAHEAD_DAYS = 14;

/**
 * A weekday is treated as part of the recurring pattern when it hosts at least
 * this share of the nights of the most-used weekday. Keeps genuine second game
 * nights while ignoring one-off makeup dates.
 */
const WEEKDAY_INCLUSION_RATIO = 0.5;

export interface NewGameSuggestion {
  /** datetime-local value ("YYYY-MM-DDTHH:mm"), or null when nothing can be inferred. */
  date: string | null;
  dateReason: string | null;
  homeTeamId: number | null;
  visitorTeamId: number | null;
  /** Why this pair of teams was chosen. */
  pairingReason: string | null;
  /** Why the home team was chosen to host. */
  hostingReason: string | null;
  gameType: Game["game_type"] | null;
  gameTypeReason: string | null;
}

export const EMPTY_SUGGESTION: NewGameSuggestion = {
  date: null,
  dateReason: null,
  homeTeamId: null,
  visitorTeamId: null,
  pairingReason: null,
  hostingReason: null,
  gameType: null,
  gameTypeReason: null,
};

/** All the games played on one calendar date, ordered by start time. */
interface GameNight {
  /** Local calendar day key, "YYYY-MM-DD". */
  key: string;
  /** Midnight local on that day. */
  day: Date;
  weekday: number;
  games: Game[];
  startTimes: Date[];
}

/**
 * Build the suggested defaults for a new game from the games already scheduled.
 *
 * @param games Recent games in the season, in any order. Games with unparseable
 *              dates are ignored.
 * @param teams The season's teams; a matchup is only suggested when at least two exist.
 */
export function suggestNewGame(
  games: Game[],
  teams: Team[],
): NewGameSuggestion {
  const nights = buildGameNights(games);
  if (nights.length === 0) return EMPTY_SUGGESTION;

  const dateSuggestion = suggestDate(nights);
  const matchup = suggestMatchup(nights, teams, dateSuggestion.date);
  const gameType = suggestGameType(nights);

  return {
    date: formatDateForInput(dateSuggestion.date),
    dateReason: dateSuggestion.reason,
    homeTeamId: matchup?.homeTeamId ?? null,
    visitorTeamId: matchup?.visitorTeamId ?? null,
    pairingReason: matchup?.pairingReason ?? null,
    hostingReason: matchup?.hostingReason ?? null,
    gameType: gameType.gameType,
    gameTypeReason: gameType.reason,
  };
}

// ---------------------------------------------------------------------------
// Date & time
// ---------------------------------------------------------------------------

/**
 * Propose when the next game should be played.
 *
 * Two cases, in order:
 *  1. The most recent night still has room (it holds fewer games than a normal
 *     night, or it is the only night on record) — take the next slot on it.
 *  2. Otherwise the night is full — roll forward to the next date that falls on
 *     one of the schedule's recurring weekdays, at that weekday's usual start time.
 */
function suggestDate(nights: GameNight[]): { date: Date; reason: string } {
  const lastNight = nights[nights.length - 1];
  const lastSlot = lastNight.startTimes[lastNight.startTimes.length - 1];
  const slotMinutes = detectSlotMinutes(nights);
  const lastNightLabel = formatGameDateWithWeekday(
    formatDateForInput(lastSlot),
  );

  if (nights.length === 1) {
    return {
      date: addMinutes(lastSlot, slotMinutes),
      reason: `the slot after your ${formatGameTime(
        formatDateForInput(lastSlot),
      )} game on ${lastNightLabel}`,
    };
  }

  // The most recent night may still be half-entered, so measure a "full" night
  // from the completed ones before it.
  const usualGamesPerNight = Math.max(
    ...nights.slice(0, -1).map((night) => night.games.length),
  );

  if (lastNight.games.length < usualGamesPerNight) {
    return {
      date: addMinutes(lastSlot, slotMinutes),
      reason: `the next open slot on ${lastNightLabel}, after the ${formatGameTime(
        formatDateForInput(lastSlot),
      )} game`,
    };
  }

  const weekdays = detectScheduledWeekdays(nights);
  const nextDay = nextScheduledDay(lastNight.day, weekdays);
  const startTime = usualStartTime(nights, nextDay.getDay());
  const date = combineDayAndTime(nextDay, startTime);
  const dateValue = formatDateForInput(date);

  return {
    date,
    reason: `your schedule repeats on ${formatWeekdayLong(
      dateValue,
    )}s at ${formatGameTime(dateValue)}`,
  };
}

/**
 * The usual gap between back-to-back games on the same night, in minutes.
 * Falls back to an hour when no night has two games to compare.
 */
function detectSlotMinutes(nights: GameNight[]): number {
  const gaps: number[] = [];

  nights.forEach((night) => {
    night.startTimes.forEach((time, index) => {
      if (index === 0) return;
      const gap = minutesBetween(night.startTimes[index - 1], time);
      // Skip double-booked slots (two rinks at once) and unrelated blocks of ice.
      if (gap > 0 && gap <= MAX_SLOT_MINUTES) gaps.push(gap);
    });
  });

  return mostCommon(gaps) ?? DEFAULT_SLOT_MINUTES;
}

/**
 * The weekdays the season regularly plays on. A weekday qualifies when it hosts
 * at least half as many nights as the most-used weekday, which keeps a genuine
 * second game night while dropping one-off makeup dates.
 */
function detectScheduledWeekdays(nights: GameNight[]): Set<number> {
  const nightsPerWeekday = new Map<number, number>();
  nights.forEach((night) => {
    nightsPerWeekday.set(
      night.weekday,
      (nightsPerWeekday.get(night.weekday) ?? 0) + 1,
    );
  });

  const busiest = Math.max(...Array.from(nightsPerWeekday.values()));
  const threshold = busiest * WEEKDAY_INCLUSION_RATIO;

  return new Set(
    Array.from(nightsPerWeekday.entries())
      .filter(([, count]) => count >= threshold)
      .map(([weekday]) => weekday),
  );
}

/** The first date after `fromDay` landing on one of the schedule's weekdays. */
function nextScheduledDay(fromDay: Date, weekdays: Set<number>): Date {
  for (let offset = 1; offset <= MAX_LOOKAHEAD_DAYS; offset += 1) {
    const candidate = addDays(fromDay, offset);
    if (weekdays.has(candidate.getDay())) return candidate;
  }
  return addDays(fromDay, 7);
}

/**
 * The time the given weekday usually starts at, taken from the first game of
 * each of that weekday's nights. Falls back to all nights when the weekday is new.
 */
function usualStartTime(nights: GameNight[], weekday: number): Date {
  const onWeekday = nights.filter((night) => night.weekday === weekday);
  const sample = onWeekday.length > 0 ? onWeekday : nights;
  const firstSlots = sample.map((night) => night.startTimes[0]);
  const usual = mostCommon(firstSlots.map(timeOfDayKey));

  if (usual) {
    const match = firstSlots.find((slot) => timeOfDayKey(slot) === usual);
    if (match) return match;
  }

  return firstSlots[firstSlots.length - 1];
}

// ---------------------------------------------------------------------------
// Matchup
// ---------------------------------------------------------------------------

interface MatchupSuggestion {
  homeTeamId: number;
  visitorTeamId: number;
  pairingReason: string;
  hostingReason: string;
}

/** Per-team and per-pairing tallies used to keep the round robin balanced. */
interface MatchupStats {
  /** Meetings so far, keyed by pairing. */
  meetings: Map<string, number>;
  /** Times a team has hosted a specific opponent, keyed by pairing and team. */
  pairHostings: Map<string, number>;
  /** Times a team has hosted anyone. */
  hostings: Map<number, number>;
  /** Games scheduled per team. */
  gamesPlayed: Map<number, number>;
  /** Chronological position of each team's most recent game; higher is newer. */
  lastPlayed: Map<number, number>;
}

/**
 * Propose the matchup that best balances the round robin: the pair that has met
 * fewest times, preferring teams with the fewest games and the longest wait.
 * Teams already booked on the suggested date are skipped where possible.
 */
function suggestMatchup(
  nights: GameNight[],
  teams: Team[],
  suggestedDate: Date,
): MatchupSuggestion | null {
  if (teams.length < 2) return null;

  const stats = buildMatchupStats(nights);
  const busy = teamsPlayingOn(nights, suggestedDate);

  // Prefer pairings that keep every team to one game a night, but fall back to
  // the full set when that leaves nothing (e.g. a two-team league).
  const available = teams.filter((team) => !busy.has(team.id));
  const candidates = buildPairings(available.length >= 2 ? available : teams);
  if (candidates.length === 0) return null;

  const best = candidates.sort((a, b) => comparePairings(a, b, stats))[0];
  const [home, visitor] = orderPairing(best, stats);
  const meetings = stats.meetings.get(pairKey(home.id, visitor.id)) ?? 0;

  return {
    homeTeamId: home.id,
    visitorTeamId: visitor.id,
    pairingReason:
      meetings === 0
        ? `${best[0].name} and ${best[1].name} haven't met yet this season`
        : `fewest meetings of any pairing (${meetings} so far)`,
    hostingReason: describeHosting(home, visitor, stats),
  };
}

function buildMatchupStats(nights: GameNight[]): MatchupStats {
  const stats: MatchupStats = {
    meetings: new Map(),
    pairHostings: new Map(),
    hostings: new Map(),
    gamesPlayed: new Map(),
    lastPlayed: new Map(),
  };

  let order = 0;
  nights.forEach((night) => {
    night.games.forEach((game) => {
      const { home_team_id: home, visitor_team_id: visitor } = game;
      const pair = pairKey(home, visitor);

      stats.meetings.set(pair, (stats.meetings.get(pair) ?? 0) + 1);
      increment(stats.pairHostings, `${pair}|${home}`);
      increment(stats.hostings, home);
      increment(stats.gamesPlayed, home);
      increment(stats.gamesPlayed, visitor);
      stats.lastPlayed.set(home, order);
      stats.lastPlayed.set(visitor, order);
      order += 1;
    });
  });

  return stats;
}

/** Teams already scheduled on the same calendar day as the suggested date. */
function teamsPlayingOn(nights: GameNight[], date: Date): Set<number> {
  const key = dayKey(date);
  const night = nights.find((candidate) => candidate.key === key);
  if (!night) return new Set();

  return new Set(
    night.games.flatMap((game) => [game.home_team_id, game.visitor_team_id]),
  );
}

function buildPairings(teams: Team[]): Array<[Team, Team]> {
  const pairings: Array<[Team, Team]> = [];
  teams.forEach((team, index) => {
    teams.slice(index + 1).forEach((opponent) => {
      pairings.push([team, opponent]);
    });
  });
  return pairings;
}

/**
 * Rank two candidate pairings. In order: fewest meetings, then fewest games
 * between the two teams, then the pair that has waited longest, then team id so
 * the suggestion is stable across reloads.
 */
function comparePairings(
  a: [Team, Team],
  b: [Team, Team],
  stats: MatchupStats,
): number {
  const meetings = (pair: [Team, Team]) =>
    stats.meetings.get(pairKey(pair[0].id, pair[1].id)) ?? 0;
  const load = (pair: [Team, Team]) =>
    (stats.gamesPlayed.get(pair[0].id) ?? 0) +
    (stats.gamesPlayed.get(pair[1].id) ?? 0);
  // The more recently played of the two decides how "rested" a pairing is.
  const recency = (pair: [Team, Team]) =>
    Math.max(
      stats.lastPlayed.get(pair[0].id) ?? -1,
      stats.lastPlayed.get(pair[1].id) ?? -1,
    );

  return (
    meetings(a) - meetings(b) ||
    load(a) - load(b) ||
    recency(a) - recency(b) ||
    a[0].id - b[0].id ||
    a[1].id - b[1].id
  );
}

/**
 * Decide which side of a pairing hosts: whoever has hosted this opponent least,
 * then whoever has hosted least overall, then the lower id for stability.
 */
function orderPairing(pair: [Team, Team], stats: MatchupStats): [Team, Team] {
  const [first, second] = pair;
  const key = pairKey(first.id, second.id);
  const hostedOpponent = (team: Team) =>
    stats.pairHostings.get(`${key}|${team.id}`) ?? 0;
  const hostedOverall = (team: Team) => stats.hostings.get(team.id) ?? 0;

  const difference =
    hostedOpponent(first) - hostedOpponent(second) ||
    hostedOverall(first) - hostedOverall(second) ||
    first.id - second.id;

  return difference <= 0 ? [first, second] : [second, first];
}

function describeHosting(
  home: Team,
  visitor: Team,
  stats: MatchupStats,
): string {
  const key = pairKey(home.id, visitor.id);
  const hostedOpponent = (team: Team) =>
    stats.pairHostings.get(`${key}|${team.id}`) ?? 0;
  const hostedOverall = (team: Team) => stats.hostings.get(team.id) ?? 0;

  if (hostedOpponent(home) < hostedOpponent(visitor)) {
    return `${visitor.name} hosted this matchup more often`;
  }
  if (hostedOverall(home) < hostedOverall(visitor)) {
    return `${home.name} hosted fewer games so far this season`;
  }
  return "hosting is even between them, so either side works";
}

// ---------------------------------------------------------------------------
// Game type
// ---------------------------------------------------------------------------

/**
 * Carry forward the game type of the most recent night, so a season that has
 * moved into playoffs keeps proposing playoff games.
 */
function suggestGameType(nights: GameNight[]): {
  gameType: Game["game_type"];
  reason: string;
} {
  const lastNight = nights[nights.length - 1];
  const gameType =
    mostCommon(lastNight.games.map((game) => game.game_type)) ?? "regular";

  return {
    gameType,
    reason: `matching your most recent games (${GAME_TYPE_LABELS[gameType]})`,
  };
}

const GAME_TYPE_LABELS: Record<Game["game_type"], string> = {
  regular: "Regular Season",
  exhibition: "Exhibition",
  playoff: "Playoffs",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Group games into calendar nights, dropping unparseable dates. Oldest first. */
function buildGameNights(games: Game[]): GameNight[] {
  const dated = games
    .map((game) => ({ game, date: parseGameDate(game.date) }))
    .filter((entry): entry is { game: Game; date: Date } => entry.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const nights = new Map<string, GameNight>();
  dated.forEach(({ game, date }) => {
    const key = dayKey(date);
    const night = nights.get(key) ?? {
      key,
      day: startOfDay(date),
      weekday: date.getDay(),
      games: [],
      startTimes: [],
    };
    night.games.push(game);
    night.startTimes.push(date);
    nights.set(key, night);
  });

  return Array.from(nights.values()).sort(
    (a, b) => a.day.getTime() - b.day.getTime(),
  );
}

function parseGameDate(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dayKey(date: Date): string {
  return formatDateForInput(date).slice(0, 10);
}

function timeOfDayKey(date: Date): string {
  return formatDateForInput(date).slice(11);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar-safe day arithmetic: the wall clock time is unaffected by DST. */
function addDays(date: Date, days: number): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + days,
    date.getHours(),
    date.getMinutes(),
  );
}

/** Calendar-safe minute arithmetic; the Date constructor normalises overflow. */
function addMinutes(date: Date, minutes: number): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes() + minutes,
  );
}

function combineDayAndTime(day: Date, time: Date): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    time.getHours(),
    time.getMinutes(),
  );
}

function minutesBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 60000);
}

function increment<K>(counter: Map<K, number>, key: K): void {
  counter.set(key, (counter.get(key) ?? 0) + 1);
}

function pairKey(teamA: number, teamB: number): string {
  return teamA < teamB ? `${teamA}-${teamB}` : `${teamB}-${teamA}`;
}

/**
 * The most frequent value, ties broken by first appearance so suggestions stay
 * stable. Returns undefined for an empty list.
 */
function mostCommon<T>(values: T[]): T | undefined {
  const counts = new Map<T, number>();
  values.forEach((value) => increment(counts, value));

  let winner: T | undefined;
  let best = 0;
  counts.forEach((count, value) => {
    if (count > best) {
      best = count;
      winner = value;
    }
  });

  return winner;
}
